import colors from './colors';
import sizes from './sizes';
import fonts from './fonts';

// Table styling system for gl2025 theme
// Mirrors buttons.js pattern with comprehensive states and variants
// Fixed: lineHeight sanitized for React Native compatibility
//
// FIGMA REQUIREMENTS:
// 1. Lock first 3 columns (checkbox, asset ID, description) on horizontal scroll
// 2. Fixed width columns with truncation (except first 3 and badge columns)
// 3. Badge/label columns should fit content (no truncation)

// Column width constants (from Figma specs - adjust after inspection)
const COLUMN_WIDTHS = Object.freeze({
    checkbox: sizes.fixedSpacing['6xl'], // 48px - Checkbox column width
    assetId: 120, // Asset ID column width
    description: 200, // Description column width
    standard: 140, // Standard data column width
    narrow: 100, // Narrow columns (e.g., quantity)
    wide: 200, // Wide columns (e.g., notes)
    min: 100, // Minimum column width constraint
    max: 250 // Maximum column width constraint
});

// Checkbox/input dimensions
const INPUT_SIZES = Object.freeze({
    checkbox: {
        width: sizes.fixedSpacing.xl, // 16px
        height: sizes.fixedSpacing.xl, // 16px
        borderWidth: 1,
        borderRadius: sizes.radius.xs, // 4px
        iconSize: sizes.fixedSpacing.lg // 12px
    }
});

// Text spacing
const END_OF_RECORDS_TEXT_MARGIN = sizes.fixedSpacing['3xl']; // 24px

// Header dimensions
const HEADER_DIMENSIONS = Object.freeze({
    height: 34, // Per Figma spec
    borderRadius: sizes.radius.md, // 8px for rounded corners
    padding: {
        vertical: sizes.fixedSpacing.md, // 8px
        horizontal: sizes.fixedSpacing.md // 8px
    },
    gap: sizes.fixedSpacing.md // 8px gap between icon and text
});

// Grid dimensions
const GRID_DIMENSIONS = Object.freeze({
    minHeight: 100, // Minimum height for grid body
    borderWidth: 1, // Standard border width
    selectedBorderWidth: 2 // Border width for selected/hover states
});

// Row dimensions
const ROW_DIMENSIONS = Object.freeze({
    padding: {
        vertical: sizes.fixedSpacing.lg, // 12px
        horizontal: sizes.fixedSpacing.md // 8px
    }
});

// Helper functions to get theme-aware colors (hides darkMode from components)
const getCheckboxColors = (darkMode, isChecked) => ({
    borderColor: isChecked 
        ? colors.primary600 
        : (darkMode ? colors.coolGray600 : colors.coolGray300),
    backgroundColor: isChecked ? colors.primary600 : 'transparent'
});

// Base styles to avoid repetition (WEB)
const baseHeader = Object.freeze({
    display: 'flex',
    alignItems: 'center',
    height: '34px', // Per Figma spec
    padding: `${sizes.fixedSpacing.md}px ${sizes.fixedSpacing.lg}px`
});

const baseCell = Object.freeze({
    display: 'flex',
    alignItems: 'center',
    padding: `${sizes.fixedSpacing.md}px ${sizes.fixedSpacing.lg}px`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

const baseRow = Object.freeze({
    display: 'flex',
    alignItems: 'center',
    // Don't set height - let parent Row control it via flexbox
    // Setting any height here covers the parent's border
    cursor: 'pointer'
    // borderBottom is set via borderBottom shorthand in row.default.primary/secondary.container
});

// Helper to convert font styles for React Native (removes string lineHeight)
const sanitizeFontForRN = (fontStyle) => {
    const { lineHeight, ...rest } = fontStyle;
    // Parse lineHeight from "16px" to 16
    const numericLineHeight = typeof lineHeight === 'string' 
        ? parseFloat(lineHeight) || undefined
        : lineHeight;
    return numericLineHeight ? { ...rest, lineHeight: numericLineHeight } : rest;
};

// Base styles for React Native (numeric values, no web-only properties)
const baseHeaderNative = Object.freeze({
    flexDirection: 'row',
    alignItems: 'center',
    height: 34, // Per Figma spec
    paddingVertical: sizes.fixedSpacing.md,
    paddingHorizontal: sizes.fixedSpacing.lg
});

const baseCellNative = Object.freeze({
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: sizes.fixedSpacing.md,
    paddingHorizontal: sizes.fixedSpacing.lg
    // Note: No textOverflow/whiteSpace/overflow - handled by RN Text numberOfLines prop
});

const baseRowNative = Object.freeze({
    flexDirection: 'row',
    alignItems: 'center'
    // Note: No cursor property in React Native
});

// Typography for different cell types
// Valid dataTypes: 'text', 'currency', 'localShortDate', 'dateTime', 'mappedBoolean', 'button', 'link', etc.
const cellTextStyles = {
    text: {
        ...fonts.text.sm.regular,
        textAlign: 'left'
    },
    currency: {
        ...fonts.text.sm.regular,
        textAlign: 'right' // Currency right-aligned
    },
    localShortDate: {
        ...fonts.text.sm.regular,
        textAlign: 'left'
    },
    mappedBoolean: {
        ...fonts.text.sm.regular,
        textAlign: 'center'
    }
};

// Main style system - organized by light/dark modes
const tableStyle = Object.freeze({
    light: {
        header: {
            default: {
                container: {
                    ...baseHeader,
                    backgroundColor: colors.coolGray600,
                    borderBottomWidth: '1px',
                    borderBottomColor: colors.coolGray300,
                    borderTopWidth: '1px',
                    borderTopColor: colors.coolGray300
                },
                text: {
                    ...fonts.text.xs.semiBold,
                    color: colors.white,
                    userSelect: 'none'
                },
                sortIcon: {
                    color: colors.white,
                    size: 'sm'
                },
                checkbox: {
                    color: colors.white,
                    size: 'md'
                }
            },
            hover: {
                container: {
                    backgroundColor: colors.coolGray700
                },
                text: {
                    color: colors.white
                }
            },
            sorted: {
                container: {
                    backgroundColor: colors.coolGray600
                },
                text: {
                    ...fonts.text.xs.semiBold,
                    color: colors.white
                },
                sortIcon: {
                    color: colors.white,
                    size: 'sm'
                }
            }
        },

        row: {
            default: {
                primary: {
                    // Odd rows
                    container: {
                        ...baseRow,
                        backgroundColor: colors.neutralGray25,
                        borderBottom: `1px solid ${colors.neutralGray300}`
                    },
                    text: {
                        ...fonts.text.sm.regular,
                        color: colors.coolGray700
                    },
                    // First column (ID) uses semiBold text
                    idText: {
                        ...fonts.text.sm.semiBold,
                        color: colors.coolGray700
                    }
                },
                secondary: {
                    // Even rows - alternating background
                    container: {
                        ...baseRow,
                        backgroundColor: colors.neutralGray50,
                        borderBottom: `1px solid ${colors.neutralGray300}`
                    },
                    text: {
                        ...fonts.text.sm.regular,
                        color: colors.coolGray700
                    },
                    idText: {
                        ...fonts.text.sm.semiBold,
                        color: colors.coolGray700
                    }
                }
            },
            hover: {
                container: {
                    backgroundColor: colors.primary50,
                    borderBottom: `2px solid ${colors.primary400}`
                },
                text: {
                    color: colors.coolGray700
                }
            },
            selected: {
                container: {
                    backgroundColor: colors.primary50,
                    borderBottom: `2px solid ${colors.primary400}`
                },
                text: {
                    color: colors.coolGray700
                }
            },
            focused: {
                container: {
                    outline: `2px solid ${colors.primary600}`,
                    outlineOffset: '-2px'
                }
            },
            // Checkbox column
            checkboxCell: {
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }
        },

        cell: {
            text: {
                container: {
                    ...baseCell
                },
                text: {
                    ...cellTextStyles.text,
                    color: colors.coolGray900
                }
            },
            currency: {
                container: {
                    ...baseCell,
                    justifyContent: 'flex-end'
                },
                text: {
                    ...cellTextStyles.currency,
                    color: colors.coolGray900
                }
            },
            localShortDate: {
                container: {
                    ...baseCell
                },
                text: {
                    ...cellTextStyles.localShortDate,
                    color: colors.coolGray900
                }
            },
            mappedBoolean: {
                container: {
                    ...baseCell,
                    justifyContent: 'center'
                },
                icon: {
                    color: colors.coolGray900,
                    size: 'sm'
                }
            }
        },

        border: {
            row: colors.coolGray200, // Subtle border between rows
            column: colors.coolGray200 // Column separators (if needed)
        }
    },

    dark: {
        header: {
            default: {
                container: {
                    ...baseHeader,
                    backgroundColor: colors.coolGray300,
                    borderBottomWidth: '1px',
                    borderBottomColor: colors.coolGray700,
                    borderTopWidth: '1px',
                    borderTopColor: colors.coolGray700
                },
                text: {
                    ...fonts.text.xs.semiBold,
                    color: colors.coolGray900,
                    userSelect: 'none'
                },
                sortIcon: {
                    color: colors.coolGray900,
                    size: 'sm'
                },
                checkbox: {
                    color: colors.coolGray900,
                    size: 'md'
                }
            },
            hover: {
                container: {
                    backgroundColor: colors.coolGray400
                },
                text: {
                    color: colors.coolGray900
                }
            },
            sorted: {
                container: {
                    backgroundColor: colors.coolGray300
                },
                text: {
                    ...fonts.text.xs.semiBold,
                    color: colors.coolGray900
                },
                sortIcon: {
                    color: colors.coolGray900,
                    size: 'sm'
                }
            }
        },

        row: {
            default: {
                primary: {
                    // Odd rows
                    container: {
                        ...baseRow,
                        backgroundColor: colors.neutralGray800,
                        borderBottom: `1px solid ${colors.coolGray700}`
                    },
                    text: {
                        ...fonts.text.sm.regular,
                        color: colors.coolGray50
                    },
                    idText: {
                        ...fonts.text.sm.semiBold,
                        color: colors.coolGray50
                    }
                },
                secondary: {
                    // Even rows - alternating background
                    container: {
                        ...baseRow,
                        backgroundColor: colors.neutralGray900,
                        borderBottom: `1px solid ${colors.coolGray700}`
                    },
                    text: {
                        ...fonts.text.sm.regular,
                        color: colors.coolGray50
                    },
                    idText: {
                        ...fonts.text.sm.semiBold,
                        color: colors.coolGray50
                    }
                }
            },
            hover: {
                container: {
                    backgroundColor: colors.primary900,
                    borderBottom: `2px solid ${colors.primary600}`
                },
                text: {
                    color: colors.coolGray50
                }
            },
            selected: {
                container: {
                    backgroundColor: colors.primary900,
                    borderBottom: `2px solid ${colors.primary600}`
                },
                text: {
                    color: colors.coolGray50
                }
            },
            focused: {
                container: {
                    outline: `2px solid ${colors.primary400}`,
                    outlineOffset: '-2px'
                }
            },
            checkboxCell: {
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }
        },

        cell: {
            text: {
                container: {
                    ...baseCell
                },
                text: {
                    ...cellTextStyles.text,
                    color: colors.coolGray50
                }
            },
            currency: {
                container: {
                    ...baseCell,
                    justifyContent: 'flex-end'
                },
                text: {
                    ...cellTextStyles.currency,
                    color: colors.coolGray50
                }
            },
            localShortDate: {
                container: {
                    ...baseCell
                },
                text: {
                    ...cellTextStyles.localShortDate,
                    color: colors.coolGray50
                }
            },
            mappedBoolean: {
                container: {
                    ...baseCell,
                    justifyContent: 'center'
                },
                icon: {
                    color: colors.coolGray50,
                    size: 'sm'
                }
            }
        },

        border: {
            row: colors.coolGray700,
            column: colors.coolGray700
        }
    }
});

// Native-specific styles (React Native compatible - numeric values, no web-only properties)
const tableStyleNative = Object.freeze({
    light: {
        header: {
            default: {
                container: {
                    ...baseHeaderNative,
                    backgroundColor: colors.coolGray600,
                    borderBottomWidth: 1,
                    borderBottomColor: colors.coolGray300,
                    borderTopWidth: 1,
                    borderTopColor: colors.coolGray300
                },
                text: {
                    ...sanitizeFontForRN(fonts.text.xs.semiBold),
                    color: colors.white
                    // Note: userSelect not applicable in RN
                },
                sortIcon: {
                    color: colors.white,
                    size: 'sm'
                },
                checkbox: {
                    color: colors.white,
                    size: 'md'
                }
            },
            hover: {
                // Note: hover states typically not used in RN (touch interface)
                container: {
                    backgroundColor: colors.coolGray700
                },
                text: {
                    color: colors.white
                }
            },
            sorted: {
                container: {
                    backgroundColor: colors.coolGray600
                },
                text: {
                    ...sanitizeFontForRN(fonts.text.xs.semiBold),
                    color: colors.white
                },
                sortIcon: {
                    color: colors.white,
                    size: 'sm'
                }
            }
        },

        row: {
            default: {
                primary: {
                    // Odd rows
                    container: {
                        ...baseRowNative,
                        backgroundColor: colors.neutralGray25,
                        borderBottomWidth: 1,
                        borderBottomColor: colors.neutralGray300
                    },
                    text: {
                        ...sanitizeFontForRN(fonts.text.sm.regular),
                        color: colors.coolGray700
                    },
                    // First column (ID) uses semiBold text
                    idText: {
                        ...sanitizeFontForRN(fonts.text.sm.semiBold),
                        color: colors.coolGray700
                    }
                },
                secondary: {
                    // Even rows - alternating background
                    container: {
                        ...baseRowNative,
                        backgroundColor: colors.neutralGray50,
                        borderBottomWidth: 1,
                        borderBottomColor: colors.neutralGray300
                    },
                    text: {
                        ...sanitizeFontForRN(fonts.text.sm.regular),
                        color: colors.coolGray700
                    },
                    idText: {
                        ...sanitizeFontForRN(fonts.text.sm.semiBold),
                        color: colors.coolGray700
                    }
                }
            },
            hover: {
                container: {
                    backgroundColor: colors.primary50,
                    borderBottomWidth: 2,
                    borderBottomColor: colors.primary400
                },
                text: {
                    color: colors.coolGray700
                }
            },
            selected: {
                container: {
                    backgroundColor: colors.primary50,
                    borderBottomWidth: 2,
                    borderBottomColor: colors.primary400
                },
                text: {
                    color: colors.coolGray700
                }
            },
            focused: {
                container: {
                    // Note: outline not supported in RN, could use borderWidth/borderColor instead
                    borderWidth: 2,
                    borderColor: colors.primary600
                }
            },
            // Checkbox column
            checkboxCell: {
                paddingVertical: 12,
                paddingHorizontal: 12,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center'
            }
        },

        cell: {
            text: {
                container: {
                    ...baseCellNative
                },
                text: {
                    ...cellTextStyles.text,
                    color: colors.coolGray900
                }
            },
            currency: {
                container: {
                    ...baseCellNative,
                    justifyContent: 'flex-end'
                },
                text: {
                    ...cellTextStyles.currency,
                    color: colors.coolGray900
                }
            },
            localShortDate: {
                container: {
                    ...baseCellNative
                },
                text: {
                    ...cellTextStyles.localShortDate,
                    color: colors.coolGray900
                }
            },
            mappedBoolean: {
                container: {
                    ...baseCellNative,
                    justifyContent: 'center'
                },
                icon: {
                    color: colors.coolGray900,
                    size: 'sm'
                }
            }
        },

        border: {
            row: colors.coolGray200,
            column: colors.coolGray200
        }
    },

    dark: {
        header: {
            default: {
                container: {
                    ...baseHeaderNative,
                    backgroundColor: colors.coolGray300,
                    borderBottomWidth: 1,
                    borderBottomColor: colors.coolGray700,
                    borderTopWidth: 1,
                    borderTopColor: colors.coolGray700
                },
                text: {
                    ...sanitizeFontForRN(fonts.text.xs.semiBold),
                    color: colors.coolGray900
                },
                sortIcon: {
                    color: colors.coolGray900,
                    size: 'sm'
                },
                checkbox: {
                    color: colors.coolGray900,
                    size: 'md'
                }
            },
            hover: {
                container: {
                    backgroundColor: colors.coolGray400
                },
                text: {
                    color: colors.coolGray900
                }
            },
            sorted: {
                container: {
                    backgroundColor: colors.coolGray300
                },
                text: {
                    ...sanitizeFontForRN(fonts.text.xs.semiBold),
                    color: colors.coolGray900
                },
                sortIcon: {
                    color: colors.coolGray900,
                    size: 'sm'
                }
            }
        },

        row: {
            default: {
                primary: {
                    // Odd rows
                    container: {
                        ...baseRowNative,
                        backgroundColor: colors.neutralGray800,
                        borderBottomWidth: 1,
                        borderBottomColor: colors.coolGray700
                    },
                    text: {
                        ...sanitizeFontForRN(fonts.text.sm.regular),
                        color: colors.coolGray50
                    },
                    idText: {
                        ...sanitizeFontForRN(fonts.text.sm.semiBold),
                        color: colors.coolGray50
                    }
                },
                secondary: {
                    // Even rows - alternating background
                    container: {
                        ...baseRowNative,
                        backgroundColor: colors.neutralGray900,
                        borderBottomWidth: 1,
                        borderBottomColor: colors.coolGray700
                    },
                    text: {
                        ...sanitizeFontForRN(fonts.text.sm.regular),
                        color: colors.coolGray50
                    },
                    idText: {
                        ...sanitizeFontForRN(fonts.text.sm.semiBold),
                        color: colors.coolGray50
                    }
                }
            },
            hover: {
                container: {
                    backgroundColor: colors.primary900,
                    borderBottomWidth: 2,
                    borderBottomColor: colors.primary600
                },
                text: {
                    color: colors.coolGray50
                }
            },
            selected: {
                container: {
                    backgroundColor: colors.primary900,
                    borderBottomWidth: 2,
                    borderBottomColor: colors.primary600
                },
                text: {
                    color: colors.coolGray50
                }
            },
            focused: {
                container: {
                    borderWidth: 2,
                    borderColor: colors.primary400
                }
            },
            checkboxCell: {
                paddingVertical: 12,
                paddingHorizontal: 12,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center'
            }
        },

        cell: {
            text: {
                container: {
                    ...baseCellNative
                },
                text: {
                    ...cellTextStyles.text,
                    color: colors.coolGray50
                }
            },
            currency: {
                container: {
                    ...baseCellNative,
                    justifyContent: 'flex-end'
                },
                text: {
                    ...cellTextStyles.currency,
                    color: colors.coolGray50
                }
            },
            localShortDate: {
                container: {
                    ...baseCellNative
                },
                text: {
                    ...cellTextStyles.localShortDate,
                    color: colors.coolGray50
                }
            },
            mappedBoolean: {
                container: {
                    ...baseCellNative,
                    justifyContent: 'center'
                },
                icon: {
                    color: colors.coolGray50,
                    size: 'sm'
                }
            }
        },

        border: {
            row: colors.coolGray700,
            column: colors.coolGray700
        }
    }
});

export default tableStyle;
export const lightTableStyle = tableStyle.light;
export const darkTableStyle = tableStyle.dark;
export const nativeTableStyle = tableStyleNative;
export { 
    COLUMN_WIDTHS, 
    cellTextStyles,
    INPUT_SIZES,
    HEADER_DIMENSIONS,
    GRID_DIMENSIONS,
    ROW_DIMENSIONS,
    END_OF_RECORDS_TEXT_MARGIN,
    getCheckboxColors
};
